if (!window.OliversFulfillment) {
  const ATTRIBUTE_KEYS = [
    'fulfillment_method',
    'pickup_location_handle',
    'pickup_location_name',
    'pickup_location_address',
    'pickup_date',
    'pickup_date_label',
    'pickup_hours',
    'pickup_selection_version'
  ];
  const DISPLAY_PROPERTY_KEYS = ['Abholort', 'Abholdatum', 'Abholzeit'];
  const INTERNAL_PROPERTY_KEY = '_olivers_fulfillment_sync';

  const root = window.Shopify?.routes?.root || '/';
  const routes = {
    cart: `${root}cart.js`,
    update: `${root}cart/update.js`,
    change: `${root}cart/change.js`
  };
  const PENDING_MARKET_KEY = 'olivers_pending_pickup_market';
  const CART_CONSTRAINT_KEY = 'olivers_cart_constraint';
  const BOOKING_WINDOW_DAYS = new Set([60, 90, 120]);
  let queue = Promise.resolve();
  let pendingOperations = 0;

  const bookingWindowDays = () => {
    const configuredDays = Number(document.body?.dataset.pickupBookingWindowDays || 60);
    return BOOKING_WINDOW_DAYS.has(configuredDays) ? configuredDays : 60;
  };

  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const setPendingMarket = handle => {
    try {
      if (handle) {
        sessionStorage.setItem(PENDING_MARKET_KEY, handle);
      } else {
        sessionStorage.removeItem(PENDING_MARKET_KEY);
      }
    } catch (error) {
      console.warn('Die Markt-Vorauswahl konnte nicht zwischengespeichert werden.', error);
    }
  };

  const getPendingMarket = () => {
    try {
      return sessionStorage.getItem(PENDING_MARKET_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const setCartConstraint = value => {
    try {
      if (value) sessionStorage.setItem(CART_CONSTRAINT_KEY, JSON.stringify(value));
      else sessionStorage.removeItem(CART_CONSTRAINT_KEY);
    } catch (error) {
      console.warn('Der Warenkorbhinweis konnte nicht zwischengespeichert werden.', error);
    }
  };

  const getCartConstraint = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(CART_CONSTRAINT_KEY) || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch (error) {
      return null;
    }
  };

  const clearCartConstraint = () => setCartConstraint(null);

  const setBusy = busy => {
    emit('olivers:fulfillment:busy', { busy });
  };

  const enqueue = operation => {
    pendingOperations += 1;
    setBusy(true);
    const run = async () => {
      try {
        return await operation();
      } finally {
        pendingOperations -= 1;
        setBusy(pendingOperations > 0);
      }
    };
    queue = queue.then(run, run);
    return queue;
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      throw new Error(`Ungültige Warenkorb-Antwort (${response.status})`, { cause: error });
    }
    if (!response.ok) {
      const message = payload?.description || payload?.message || `Warenkorb-Anfrage fehlgeschlagen (${response.status})`;
      throw new Error(message);
    }
    return payload;
  };

  const parseLocations = value => {
    try {
      const locations = JSON.parse(value || '[]');
      return Array.isArray(locations) ? locations : null;
    } catch (error) {
      console.error('Abholmärkte konnten nicht gelesen werden.', error);
      return null;
    }
  };

  const getLocations = () => {
    const dataNode = document.getElementById('PickupLocationsData');
    return dataNode ? (parseLocations(dataNode.textContent) || []) : [];
  };

  const refreshLocations = async () => {
    const url = new URL(`${root}cart`, window.location.origin);
    url.searchParams.set('_olivers_availability_check', String(Date.now()));
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
        'Cache-Control': 'no-cache'
      }
    });
    if (!response.ok) throw new Error(`Marktdaten konnten nicht aktualisiert werden (${response.status}).`);

    const html = await response.text();
    const freshDocument = new DOMParser().parseFromString(html, 'text/html');
    const freshDataNode = freshDocument.getElementById('PickupLocationsData');
    const locations = freshDataNode ? parseLocations(freshDataNode.textContent) : null;
    if (!locations) throw new Error('Die aktuellen Marktdaten konnten nicht gelesen werden.');

    const currentDataNode = document.getElementById('PickupLocationsData');
    if (!currentDataNode) throw new Error('Die Marktdaten sind auf dieser Seite nicht verfügbar.');
    currentDataNode.textContent = JSON.stringify(locations);
    emit('olivers:fulfillment:locations-updated', { locations });
    return locations;
  };

  const activeLocations = () => getLocations()
    .filter(location => location && location.active === true && location.handle)
    .sort((first, second) => Number(first.sortOrder || 999) - Number(second.sortOrder || 999));

  const findLocation = handle => activeLocations().find(location => location.handle === handle) || null;
  const locationAllowsHalfCheesecakes = location => location?.allowHalfCheesecakes === true;
  const locationAllowsMerchPickup = location => location?.allowMerchPickup === true;

  const baseAttributes = method => ({
    fulfillment_method: method || '',
    pickup_location_handle: '',
    pickup_location_name: '',
    pickup_location_address: '',
    pickup_date: '',
    pickup_date_label: '',
    pickup_hours: '',
    pickup_selection_version: '1'
  });

  const attributesForMarket = location => ({
    ...baseAttributes('pickup'),
    pickup_location_handle: location.handle,
    pickup_location_name: String(location.name || ''),
    pickup_location_address: String(location.address || '')
  });

  const formatDate = date => {
    if (window.OliversPickupAvailability?.formatDate) {
      return window.OliversPickupAvailability.formatDate(date);
    }
    return date;
  };

  const attributesForPickup = (location, date) => ({
    ...attributesForMarket(location),
    pickup_date: date,
    pickup_date_label: formatDate(date),
    pickup_hours: String(location.hours || '')
  });

  const knownAttributes = attributes => ATTRIBUTE_KEYS.reduce((result, key) => {
    result[key] = String(attributes?.[key] ?? '');
    return result;
  }, {});

  const displayPropertiesForAttributes = attributes => {
    const values = knownAttributes(attributes);
    if (
      values.fulfillment_method !== 'pickup' ||
      !values.pickup_location_name ||
      !values.pickup_date_label ||
      !values.pickup_hours
    ) return {};
    return {
      Abholort: values.pickup_location_name,
      Abholdatum: values.pickup_date_label,
      Abholzeit: values.pickup_hours
    };
  };

  const marketDataProductIds = () => new Set(
    Array.from(document.querySelectorAll('[data-js-cart-item][data-oc-market-data="true"][data-product-id]'))
      .map(item => String(item.dataset.productId || ''))
      .filter(Boolean)
  );

  const merchandiseProductIds = () => new Set(
    Array.from(document.querySelectorAll('[data-js-cart-item][data-oc-merch="true"][data-product-id]'))
      .map(item => String(item.dataset.productId || ''))
      .filter(Boolean)
  );

  const shippableProductIds = () => new Set(
    Array.from(document.querySelectorAll('[data-js-cart-item][data-oc-shippable="true"][data-product-id]'))
      .map(item => String(item.dataset.productId || ''))
      .filter(Boolean)
  );

  const voucherProductIds = () => new Set(
    Array.from(document.querySelectorAll('[data-js-cart-item][data-oc-voucher="true"][data-product-id]'))
      .map(item => String(item.dataset.productId || ''))
      .filter(Boolean)
  );

  const marketDataTargetIndex = cart => {
    const eligibleProductIds = marketDataProductIds();
    return (cart?.items || []).findIndex(item => eligibleProductIds.has(String(item.product_id || '')));
  };

  const cartContainsMerchandise = cart => {
    const eligibleProductIds = merchandiseProductIds();
    return (cart?.items || []).some(item => eligibleProductIds.has(String(item.product_id || '')));
  };

  const cartRequiresFulfillment = cart => marketDataTargetIndex(cart) !== -1 || cartContainsMerchandise(cart)
    || (cart?.items || []).some(item => voucherProductIds().has(String(item.product_id || '')))
    || (cart?.items || []).some(item => shippableProductIds().has(String(item.product_id || '')));

  const pickupDisplayTargetIndex = cart => {
    const marketIndex = marketDataTargetIndex(cart);
    if (marketIndex !== -1) return marketIndex;
    const eligibleProductIds = new Set([...merchandiseProductIds(), ...voucherProductIds(), ...shippableProductIds()]);
    return (cart?.items || []).findIndex(item => eligibleProductIds.has(String(item.product_id || '')));
  };

  const cartDeliveryMethod = cart => marketDataTargetIndex(cart) !== -1 ? 'frozen_delivery' : 'postal_delivery';
  const cartAllShippable = cart => {
    const items = cart?.items || [];
    const productIds = shippableProductIds();
    return items.length > 0 && items.every(item => productIds.has(String(item.product_id || '')));
  };
  const deliveryEnabled = () => document.body?.dataset.frozenDeliveryEnabled === 'true';
  const deliveryAvailable = cart => deliveryEnabled() && cartAllShippable(cart) && !cartContainsHalfCheesecake(cart);

  const isHalfVariantTitle = value => /(^|\s|[-_/])(halb(?:e[rsnm]?)?|half|1\s*\/\s*2|0[,.]5|½)(\s|$|[-_/])/i.test(` ${String(value || '').trim()} `);

  const halfMarketDataVariantIds = () => new Set(
    Array.from(document.querySelectorAll('[data-js-cart-item][data-oc-market-data="true"][data-oc-size="half"][data-variant-id]'))
      .map(item => String(item.dataset.variantId || ''))
      .filter(Boolean)
  );

  const cartContainsHalfCheesecake = cart => {
    const eligibleProductIds = marketDataProductIds();
    const halfVariantIds = halfMarketDataVariantIds();
    return (cart?.items || []).some(item => (
      halfVariantIds.has(String(item.variant_id || '')) || (
        eligibleProductIds.has(String(item.product_id || '')) && isHalfVariantTitle(item.variant_title)
      )
    ));
  };

  const propertiesEqual = (first, second) => {
    const firstKeys = Object.keys(first || {}).sort();
    const secondKeys = Object.keys(second || {}).sort();
    return firstKeys.length === secondKeys.length && firstKeys.every((key, index) => (
      key === secondKeys[index] && String(first?.[key] ?? '') === String(second?.[key] ?? '')
    ));
  };

  const synchronizeDisplayProperties = async (cart, attributes) => {
    let currentCart = cart;
    const desiredDisplayProperties = displayPropertiesForAttributes(attributes);
    const maximumUpdates = Math.max(4, Number(cart?.items?.length || 0) * 3 + 3);

    for (let updateCount = 0; updateCount < maximumUpdates; updateCount += 1) {
      const items = currentCart?.items || [];
      const targetIndex = Object.keys(desiredDisplayProperties).length
        ? pickupDisplayTargetIndex(currentCart)
        : -1;
      let pendingChange = null;

      items.some((item, index) => {
        const currentProperties = item.properties || {};
        const properties = { ...currentProperties };
        const hadDisplayProperties = DISPLAY_PROPERTY_KEYS.some(key => Object.prototype.hasOwnProperty.call(currentProperties, key));
        DISPLAY_PROPERTY_KEYS.forEach(key => { delete properties[key]; });
        if (index === targetIndex) Object.assign(properties, desiredDisplayProperties);
        if (hadDisplayProperties && Object.keys(properties).length === 0) {
          properties[INTERNAL_PROPERTY_KEY] = '1';
        }
        if (!propertiesEqual(currentProperties, properties)) {
          pendingChange = { id: item.key, properties };
          return true;
        }
        return false;
      });

      if (!pendingChange) return currentCart;
      currentCart = await requestJson(routes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingChange)
      });
    }

    throw new Error('Die sichtbaren Abholdaten konnten nicht eindeutig synchronisiert werden.');
  };

  const availableDates = (location, options = {}) => {
    if (!window.OliversPickupAvailability?.availableDates) return [];
    const availabilityOptions = typeof options === 'number' ? { limit: options } : options;
    return window.OliversPickupAvailability.availableDates(location, {
      limit: 200,
      searchDays: bookingWindowDays(),
      ...availabilityOptions
    });
  };

  const validate = (attributes, options = {}) => {
    const values = knownAttributes(attributes);
    const requireDate = options.requireDate !== false;
    const containsHalfCheesecake = options.containsHalfCheesecake === true;
    const containsMerchandise = options.containsMerchandise === true;
    if (values.fulfillment_method === 'frozen_delivery' || values.fulfillment_method === 'postal_delivery') {
      if (containsHalfCheesecake) {
        return {
          valid: false,
          complete: false,
          method: values.fulfillment_method,
          location: null,
          reason: 'half_not_shippable'
        };
      }
      if (options.deliveryEnabled === false) {
        return { valid: false, complete: false, method: values.fulfillment_method, location: null, reason: 'delivery_disabled' };
      }
      if (options.allShippable !== true) {
        return { valid: false, complete: false, method: values.fulfillment_method, location: null, reason: 'products_not_shippable' };
      }
      if (values.fulfillment_method !== options.deliveryMethod) {
        return { valid: false, complete: false, method: values.fulfillment_method, location: null, reason: 'delivery_method_changed' };
      }
      return {
        valid: true,
        complete: true,
        method: values.fulfillment_method,
        location: null,
        reason: ''
      };
    }
    if (values.fulfillment_method !== 'pickup') {
      return { valid: false, complete: false, method: values.fulfillment_method, location: null, reason: 'method_missing' };
    }
    const location = findLocation(values.pickup_location_handle);
    if (!location) {
      return { valid: false, complete: false, method: 'pickup', location: null, reason: 'location_invalid' };
    }
    if (containsHalfCheesecake && !locationAllowsHalfCheesecakes(location)) {
      return { valid: false, complete: false, method: 'pickup', location, reason: 'half_not_allowed_at_market' };
    }
    if (containsMerchandise && !locationAllowsMerchPickup(location)) {
      return { valid: false, complete: false, method: 'pickup', location, reason: 'merch_not_allowed_at_market' };
    }
    if (!values.pickup_date) {
      return {
        valid: !requireDate,
        complete: false,
        method: 'pickup',
        location,
        reason: requireDate ? 'date_missing' : ''
      };
    }
    if (!availableDates(location).includes(values.pickup_date)) {
      return { valid: false, complete: false, method: 'pickup', location, reason: 'date_invalid' };
    }
    return { valid: true, complete: true, method: 'pickup', location, reason: '' };
  };

  const readCart = () => enqueue(() => requestJson(routes.cart));

  const updateAttributes = attributes => enqueue(async () => {
    const values = knownAttributes(attributes);
    let cart = await requestJson(routes.update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: values })
    });
    cart = await synchronizeDisplayProperties(cart, values);
    emit('olivers:fulfillment:updated', { attributes: values, cart });
    return { attributes: values, cart };
  });

  const refreshDisplayProperties = () => enqueue(async () => {
    let cart = await requestJson(routes.cart);
    const attributes = knownAttributes(cart.attributes || {});
    cart = await synchronizeDisplayProperties(cart, attributes);
    emit('olivers:fulfillment:updated', { attributes, cart });
    return { attributes, cart };
  });

  const selectMarket = handle => {
    const location = findLocation(handle);
    if (!location) return Promise.reject(new Error('Der gewählte Abholmarkt ist nicht verfügbar.'));
    if (!availableDates(location, { limit: 1 }).length) {
      return Promise.reject(new Error('Für diesen Markt sind aktuell keine Bestellungen möglich. Neue Termine folgen.'));
    }
    return updateAttributes(attributesForMarket(location));
  };

  const selectPickup = (handle, date) => {
    const location = findLocation(handle);
    if (!location) return Promise.reject(new Error('Der gewählte Abholmarkt ist nicht verfügbar.'));
    if (!availableDates(location).includes(date)) {
      return Promise.reject(new Error('Der gewählte Abholtermin ist nicht mehr verfügbar.'));
    }
    return updateAttributes(attributesForPickup(location, date));
  };

  const selectDelivery = async () => {
    const cart = await readCart();
    if (!deliveryAvailable(cart)) throw new Error('Nicht alle Artikel in diesem Warenkorb können versendet werden.');
    return updateAttributes(baseAttributes(cartDeliveryMethod(cart)));
  };
  const clear = method => updateAttributes(baseAttributes(method || ''));

  const productRules = productForm => {
    try {
      const parsed = JSON.parse(productForm?.querySelector('[data-oc-product-rules]')?.textContent || '{}');
      return {
        isMarketProduct: parsed?.isMarketProduct === true,
        isMerchProduct: parsed?.isMerchProduct === true,
        halfVariantIds: new Set((parsed?.halfVariantIds || []).map(String))
      };
    } catch (error) {
      console.error('Die Produktregeln konnten nicht gelesen werden.', error);
      return { isMarketProduct: false, isMerchProduct: false, halfVariantIds: new Set() };
    }
  };

  const productFormConstraint = (form, productForm) => {
    const rules = productRules(productForm);
    const variantId = String(form?.querySelector('[name="id"]')?.value || '');
    return {
      addsHalfCheesecake: rules.isMarketProduct && rules.halfVariantIds.has(variantId),
      addsMerchandise: rules.isMerchProduct
    };
  };

  const guardProductAdd = async (form, productForm) => {
    const constraint = productFormConstraint(form, productForm);
    if (!constraint.addsHalfCheesecake && !constraint.addsMerchandise) return true;
    try {
      const cart = await readCart();
      const attributes = knownAttributes(cart.attributes || {});
      setCartConstraint({
        pending: true,
        addsHalfCheesecake: constraint.addsHalfCheesecake,
        addsMerchandise: constraint.addsMerchandise,
        previousMethod: attributes.fulfillment_method,
        previousMarketName: attributes.pickup_location_name
      });
    } catch (error) {
      console.error(error);
      clearCartConstraint();
    }
    return true;
  };

  const reconcileCartConstraints = async () => {
    let cart = await readCart();
    const pending = getCartConstraint();
    const attributes = knownAttributes(cart.attributes || {});
    if (attributes.fulfillment_method === 'pickup' && attributes.pickup_location_handle) {
      await refreshLocations();
    }
    const containsHalfCheesecake = cartContainsHalfCheesecake(cart) || pending?.addsHalfCheesecake === true;
    const containsMerchandise = cartContainsMerchandise(cart) || pending?.addsMerchandise === true;
    const selectedDelivery = attributes.fulfillment_method === 'frozen_delivery' || attributes.fulfillment_method === 'postal_delivery';
    const currentDeliveryMethod = cartDeliveryMethod(cart);
    const canDeliver = deliveryAvailable(cart) && !pending?.addsHalfCheesecake;
    const selectedLocation = findLocation(attributes.pickup_location_handle);
    const halfMarketInvalid = containsHalfCheesecake && selectedLocation && !locationAllowsHalfCheesecakes(selectedLocation);
    const merchMarketInvalid = containsMerchandise && selectedLocation && !locationAllowsMerchPickup(selectedLocation);
    let reason = '';

    if (containsHalfCheesecake && selectedDelivery) {
      reason = 'half_delivery_switched';
    } else if (selectedDelivery && !canDeliver) {
      reason = deliveryEnabled() ? 'products_delivery_switched' : 'delivery_disabled_switched';
    } else if (attributes.fulfillment_method === 'pickup' && attributes.pickup_location_handle && (!selectedLocation || halfMarketInvalid || merchMarketInvalid)) {
      if (halfMarketInvalid && merchMarketInvalid) reason = 'combined_market_reset';
      else if (halfMarketInvalid) reason = 'half_market_reset';
      else if (merchMarketInvalid) reason = 'merch_market_reset';
      else reason = 'market_reset';
    } else if (containsHalfCheesecake && attributes.fulfillment_method !== 'pickup') {
      reason = 'half_pickup_required';
    }

    if (reason) {
      const marketName = attributes.pickup_location_name || pending?.previousMarketName || '';
      const result = await clear('pickup');
      cart = result.cart;
      const notice = { reason, marketName };
      setCartConstraint(notice);
      emit('olivers:cart-constraints', {
        notice,
        cart,
        attributes: result.attributes,
        containsHalfCheesecake,
        containsMerchandise
      });
      return { notice, cart, attributes: result.attributes, containsHalfCheesecake, containsMerchandise };
    }

    if (selectedDelivery && attributes.fulfillment_method !== currentDeliveryMethod) {
      const result = await updateAttributes(baseAttributes(currentDeliveryMethod));
      clearCartConstraint();
      return { notice: null, cart: result.cart, attributes: result.attributes, containsHalfCheesecake, containsMerchandise };
    }

    const noticeStillRelevant = pending?.reason && (
      ((pending.reason === 'half_delivery_switched' || pending.reason === 'half_pickup_required' || pending.reason === 'half_market_reset') && containsHalfCheesecake)
      || (pending.reason === 'products_delivery_switched' && !cartAllShippable(cart))
      || (pending.reason === 'delivery_disabled_switched' && !deliveryEnabled())
      || (pending.reason === 'merch_market_reset' && containsMerchandise)
      || ((pending.reason === 'combined_market_reset' || pending.reason === 'market_reset') && (containsHalfCheesecake || containsMerchandise))
    ) && attributes.fulfillment_method === 'pickup' && !attributes.pickup_location_handle;
    const notice = noticeStillRelevant ? pending : null;
    if (!notice) clearCartConstraint();
    const result = await refreshDisplayProperties();
    emit('olivers:cart-constraints', {
      notice,
      cart: result.cart,
      attributes: result.attributes,
      containsHalfCheesecake,
      containsMerchandise
    });
    return { notice, cart: result.cart, attributes: result.attributes, containsHalfCheesecake, containsMerchandise };
  };

  const attributesEqual = (first, second) => ATTRIBUTE_KEYS.every(key => String(first?.[key] ?? '') === String(second?.[key] ?? ''));

  const ensureCheckoutReady = async deliveryEnabled => {
    const cart = await readCart();
    if (!cart.item_count) return { valid: false, reason: 'cart_empty', cart };
    if (!cartRequiresFulfillment(cart)) {
      const clearedAttributes = baseAttributes('');
      if (!attributesEqual(knownAttributes(cart.attributes || {}), clearedAttributes)) {
        const result = await updateAttributes(clearedAttributes);
        return {
          valid: true,
          complete: true,
          method: 'standard',
          reason: '',
          cart: result.cart,
          attributes: result.attributes
        };
      }
      const synchronized = await enqueue(() => synchronizeDisplayProperties(cart, clearedAttributes));
      return {
        valid: true,
        complete: true,
        method: 'standard',
        reason: '',
        cart: synchronized,
        attributes: clearedAttributes
      };
    }
    if (cart.attributes?.fulfillment_method === 'pickup') await refreshLocations();
    const containsHalfCheesecake = cartContainsHalfCheesecake(cart);
    const containsMerchandise = cartContainsMerchandise(cart);
    const validation = validate(cart.attributes || {}, {
      deliveryEnabled,
      containsHalfCheesecake,
      containsMerchandise,
      allShippable: cartAllShippable(cart),
      deliveryMethod: cartDeliveryMethod(cart)
    });
    if (!validation.complete) return { ...validation, cart };
    const canonicalAttributes = validation.method === 'pickup'
      ? attributesForPickup(validation.location, String(cart.attributes?.pickup_date || ''))
      : baseAttributes(validation.method);
    if (!attributesEqual(knownAttributes(cart.attributes || {}), canonicalAttributes)) {
      const result = await updateAttributes(canonicalAttributes);
      return { ...validation, cart: result.cart, attributes: result.attributes };
    }
    const synchronized = await enqueue(() => synchronizeDisplayProperties(cart, canonicalAttributes));
    return { ...validation, cart: synchronized, attributes: knownAttributes(cart.attributes || {}) };
  };

  const cleanMarketParameter = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('pickup_market')) return;
    url.searchParams.delete('pickup_market');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const applyPendingMarket = async (options = {}) => {
    const handle = options.handle || getPendingMarket();
    if (!handle) return null;
    const location = findLocation(handle);
    if (!location) {
      setPendingMarket('');
      if (options.cleanUrl !== false) cleanMarketParameter();
      emit('olivers:pickup-preselection:error', { reason: 'location_invalid' });
      return null;
    }
    if (!availableDates(location, { limit: 1 }).length) {
      setPendingMarket('');
      if (options.cleanUrl !== false) cleanMarketParameter();
      emit('olivers:pickup-preselection:error', { reason: 'date_unavailable', location });
      return null;
    }
    setPendingMarket(handle);
    emit('olivers:pickup-preselection:saving', { location });
    try {
      const result = await selectMarket(handle);
      setPendingMarket('');
      if (options.cleanUrl !== false) cleanMarketParameter();
      emit('olivers:pickup-preselection:success', { location, cart: result.cart });
      return result;
    } catch (error) {
      emit('olivers:pickup-preselection:error', { reason: 'save_failed', location, error });
      throw error;
    }
  };

  const bootPreselection = () => {
    const url = new URL(window.location.href);
    const handle = url.searchParams.get('pickup_market') || getPendingMarket();
    if (handle) applyPendingMarket({ handle }).catch(error => console.error(error));
  };

  window.OliversFulfillment = Object.freeze({
    ATTRIBUTE_KEYS,
    DISPLAY_PROPERTY_KEYS,
    INTERNAL_PROPERTY_KEY,
    routes,
    getLocations,
    refreshLocations,
    activeLocations,
    findLocation,
    locationAllowsHalfCheesecakes,
    locationAllowsMerchPickup,
    baseAttributes,
    attributesForMarket,
    attributesForPickup,
    knownAttributes,
    displayPropertiesForAttributes,
    cartRequiresFulfillment,
    cartContainsHalfCheesecake,
    cartContainsMerchandise,
    cartAllShippable,
    cartDeliveryMethod,
    getCartConstraint,
    clearCartConstraint,
    availableDates,
    validate,
    synchronizeDisplayProperties,
    refreshDisplayProperties,
    readCart,
    updateAttributes,
    selectMarket,
    selectPickup,
    selectDelivery,
    clear,
    guardProductAdd,
    reconcileCartConstraints,
    applyPendingMarket,
    ensureCheckoutReady
  });

  document.addEventListener('krown:cart:add', async () => {
    try {
      if (getPendingMarket()) await applyPendingMarket({ cleanUrl: false });
      await reconcileCartConstraints();
    } catch (error) {
      console.error(error);
    }
  });
  document.addEventListener('krown:cart:update', () => {
    reconcileCartConstraints().catch(error => console.error(error));
  });
  window.addEventListener('online', () => {
    if (getPendingMarket()) applyPendingMarket().catch(error => console.error(error));
  });

  const bypassedForms = new WeakSet();
  const guardCheckout = async (form, submitter) => {
    if (!form || form.dataset.fulfillmentChecking === 'true') return;
    form.dataset.fulfillmentChecking = 'true';
    if (submitter) submitter.disabled = true;
    let selectionBlocked = false;
    try {
      const deliveryEnabled = document.body?.dataset.frozenDeliveryEnabled === 'true';
      const result = await window.OliversFulfillment.ensureCheckoutReady(deliveryEnabled);
      if (!result.valid || !result.complete) {
        selectionBlocked = true;
        emit('olivers:checkout:blocked', result);
        const cartPath = `${root}cart`;
        if (!form.closest('[data-cart-subtotal]')) window.location.assign(cartPath);
        return;
      }
      bypassedForms.add(form);
      form.requestSubmit(submitter || undefined);
    } catch (error) {
      console.error(error);
      emit('olivers:checkout:blocked', { reason: 'save_failed', error });
    } finally {
      delete form.dataset.fulfillmentChecking;
      if (selectionBlocked) {
        window.refreshPickupCheckoutState?.();
      } else if (submitter && !bypassedForms.has(form)) {
        submitter.disabled = false;
      }
    }
  };

  document.addEventListener('click', event => {
    const submitter = event.target.closest('[name="checkout"]');
    if (!submitter || !submitter.form || bypassedForms.has(submitter.form)) return;
    event.preventDefault();
    guardCheckout(submitter.form, submitter);
  }, true);

  document.addEventListener('submit', event => {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (bypassedForms.has(event.target)) {
      bypassedForms.delete(event.target);
      return;
    }
    event.preventDefault();
    guardCheckout(event.target, submitter);
  }, true);

  bootPreselection();
}
